import { Button } from "@/components/flexkit/Button";
import { Input } from "@/components/podkit/forms/Input";
import { Label } from "@/components/podkit/forms/Label";
import { RadioGroup, RadioGroupItem } from "@/components/podkit/forms/RadioListField";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { cn } from "@/components/podkit/lib/cn";
import { useCreateWebhook } from "@/queries/webhook-queries";
import { WebhookProvider, WebhookType } from "gitpod-next-api/gitpod/v1/webhook_pb";
import { useId, useMemo, useRef, type FC } from "react";
import { formatError } from "@/utils/errors";
import { atom, useAtom, useStore, type PrimitiveAtom } from "jotai";
import { useRadioGroup, useRadio, useFocusRing, mergeProps, VisuallyHidden } from "react-aria";
import { useRadioGroupState } from "react-stately";
import type { AriaRadioGroupProps, AriaRadioProps } from "react-aria";
import { IconGitHub } from "@/assets/icons/geist/IconGitHub";
import { IconGitLab } from "@/assets/icons/geist/IconGitLab";

type CreateWebhookFormProps = {
    onCancel: () => void;
    onSuccess: (webhookId: string) => void;
};

type FormErrorMessages = {
    name?: string;
    type?: string;
    provider?: string;
};

type FormAtoms = {
    nameAtom: PrimitiveAtom<string>;
    typeAtom: PrimitiveAtom<WebhookType | null>;
    providerAtom: PrimitiveAtom<WebhookProvider | null>;
    errorMessagesAtom: PrimitiveAtom<FormErrorMessages>;
};

export const CreateWebhookForm: FC<CreateWebhookFormProps> = ({ onCancel, onSuccess }) => {
    const { toast } = useToast();
    const { mutateAsync: createWebhook, isPending } = useCreateWebhook();
    const store = useStore();
    const atoms = useFormAtoms();
    const formId = useId();

    const resetForm = () => {
        store.set(atoms.nameAtom, "");
        store.set(atoms.typeAtom, null);
        store.set(atoms.providerAtom, null);
        store.set(atoms.errorMessagesAtom, {});
    };

    const validateForm = (): boolean => {
        const name = store.get(atoms.nameAtom);
        const type = store.get(atoms.typeAtom);
        const provider = store.get(atoms.providerAtom);

        const newErrorMessages: FormErrorMessages = {};

        if (!name.trim()) {
            newErrorMessages.name = "Please provide a name for this webhook";
        } else if (name.length > 80) {
            newErrorMessages.name = "Name must be 80 characters or less";
        }

        if (!type) {
            newErrorMessages.type = "Please select whether this webhook is for a repository or organization";
        }

        if (!provider) {
            newErrorMessages.provider = "Please select a Git provider";
        }

        store.set(atoms.errorMessagesAtom, newErrorMessages);
        return Object.keys(newErrorMessages).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        const name = store.get(atoms.nameAtom);
        const type = store.get(atoms.typeAtom);
        const provider = store.get(atoms.providerAtom);

        try {
            const webhook = await createWebhook({
                name: name.trim(),
                type: type!,
                provider: provider!,
            });
            resetForm();
            onSuccess(webhook.id);
        } catch (error) {
            toast({
                title: "Failed to create webhook",
                description: formatError(error),
            });
        }
    };

    return (
        <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-4 pb-5">
            <LocationField atoms={atoms} />
            <WebhookTypeField atoms={atoms} />
            <WebhookNameField atoms={atoms} />
            <div className="flex justify-end gap-2 pt-2">
                <Button
                    variant="outline"
                    type="button"
                    onClick={onCancel}
                    data-testid="webhook-cancel"
                    data-tracking-id="cancel"
                >
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    type="submit"
                    loading={isPending}
                    disabled={isPending}
                    data-testid="webhook-create-submit"
                    data-tracking-id="create-webhook-submit"
                >
                    Create Webhook
                </Button>
            </div>
        </form>
    );
};

function useFormAtoms(): FormAtoms {
    return useMemo(() => {
        const nameAtom = atom("");
        const typeAtom = atom<WebhookType | null>(null);
        const providerAtom = atom<WebhookProvider | null>(null);
        const errorMessagesAtom = atom<FormErrorMessages>({});

        return {
            nameAtom,
            typeAtom,
            providerAtom,
            errorMessagesAtom,
        };
    }, []);
}

const LocationField: FC<{ atoms: FormAtoms }> = ({ atoms }) => {
    const [provider, setProvider] = useAtom(atoms.providerAtom);
    const [errorMessages] = useAtom(atoms.errorMessagesAtom);

    const handleChange = (value: string) => {
        setProvider(parseInt(value) as WebhookProvider);
    };

    const radioGroupProps: AriaRadioGroupProps = {
        label: "Location",
        value: provider?.toString() ?? "",
        onChange: handleChange,
    };

    const state = useRadioGroupState(radioGroupProps);
    const { radioGroupProps: groupProps, labelProps } = useRadioGroup(radioGroupProps, state);

    return (
        <div {...groupProps} className="flex select-none flex-col">
            <span {...labelProps} className="mb-2 text-sm font-medium">
                Location
            </span>
            <div className="flex gap-3">
                <LocationRadioOption
                    value={WebhookProvider.GITHUB.toString()}
                    state={state}
                    testId="webhook-provider-github"
                >
                    <IconGitHub size="sm" className="text-content-primary" />
                    <span className="text-sm text-content-primary">GitHub</span>
                </LocationRadioOption>
                <LocationRadioOption
                    value={WebhookProvider.GITLAB.toString()}
                    state={state}
                    testId="webhook-provider-gitlab"
                >
                    <IconGitLab size="sm" className="text-content-primary" />
                    <span className="text-sm text-content-primary">GitLab</span>
                </LocationRadioOption>
            </div>
            {errorMessages.provider && (
                <span className="text-sm text-content-destructive animate-in fade-in">{errorMessages.provider}</span>
            )}
        </div>
    );
};

type LocationRadioOptionProps = {
    value: string;
    state: ReturnType<typeof useRadioGroupState>;
    testId: string;
    children: React.ReactNode;
};

const LocationRadioOption: FC<LocationRadioOptionProps> = ({ value, state, testId, children }) => {
    const ref = useRef<HTMLInputElement>(null);
    const radioProps: AriaRadioProps = { value };
    const { inputProps, isSelected } = useRadio(radioProps, state, ref);
    const { isFocusVisible, focusProps } = useFocusRing();

    return (
        <label
            className={cn(
                "flex cursor-pointer items-center gap-2 rounded-lg p-3 outline-none transition-colors",
                isSelected
                    ? "border-[1.5px] border-border-brand bg-surface-brand-subtle shadow"
                    : "border border-border-base",
                // Compensate for border width difference to prevent layout shift
                !isSelected && "m-[0.5px]",
                isFocusVisible && "focus:ring-2 focus:ring-border-brand focus:ring-offset-2",
            )}
            data-testid={testId}
        >
            <VisuallyHidden>
                <input {...mergeProps(inputProps, focusProps)} ref={ref} />
            </VisuallyHidden>
            {children}
        </label>
    );
};

const WebhookTypeField: FC<{ atoms: FormAtoms }> = ({ atoms }) => {
    const [type, setType] = useAtom(atoms.typeAtom);
    const [errorMessages] = useAtom(atoms.errorMessagesAtom);

    const handleTypeChange = (value: string) => {
        setType(parseInt(value) as WebhookType);
    };

    return (
        <div className="flex flex-col text-sm">
            <span className="mb-2 font-medium">Type</span>
            <RadioGroup value={type?.toString() ?? ""} onValueChange={handleTypeChange} className="flex flex-row gap-4">
                <label className="flex cursor-pointer flex-row items-center gap-2">
                    <RadioGroupItem value={WebhookType.SCM_ORGANIZATION.toString()} />
                    <span className="text-sm">Organization</span>
                </label>
                <label className="flex cursor-pointer flex-row items-center gap-2">
                    <RadioGroupItem value={WebhookType.SCM_REPOSITORY.toString()} />
                    <span className="text-sm">Repository</span>
                </label>
            </RadioGroup>
            {errorMessages.type && (
                <span className="text-sm text-content-destructive animate-in fade-in">{errorMessages.type}</span>
            )}
        </div>
    );
};

const WebhookNameField: FC<{ atoms: FormAtoms }> = ({ atoms }) => {
    const [name, setName] = useAtom(atoms.nameAtom);
    const [errorMessages] = useAtom(atoms.errorMessagesAtom);

    return (
        <div className="flex flex-col gap-2">
            <Label className="text-sm" htmlFor="webhook-name">
                Name
            </Label>
            <Input
                data-1p-ignore
                data-lpignore="true"
                data-protonpass-ignore="true"
                id="webhook-name"
                placeholder="My Webhook"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="webhook-name-input"
                maxLength={80}
            />
            {errorMessages.name && (
                <span className="text-sm text-content-destructive animate-in fade-in">{errorMessages.name}</span>
            )}
        </div>
    );
};
